<template>
  <el-dialog
    v-model="dialogVisible"
    :title="lockPeriod ? 'Hisobotni almashtirish' : 'Hisobot joylash'"
    width="560px"
    append-to-body
    :close-on-click-modal="false"
    :close-on-press-escape="!busy"
    :show-close="!busy"
    @closed="reset"
  >
    <div class="mfr-dialog">
      <el-alert
        v-if="lockPeriod"
        type="warning"
        show-icon
        :closable="false"
        class="mfr-dialog__notice"
        :title="`${year}-yil ${quarter}-chorak hisobotini almashtirasiz. Avvalgi versiya foydalanuvchilarga ko'rinmaydi.`"
      />

      <el-row :gutter="12">
        <el-col :span="12">
          <label class="mfr-field__label" for="mfr-year">Yil</label>
          <el-select
            id="mfr-year"
            v-model="year"
            :disabled="lockPeriod || busy"
            style="width: 100%"
            placeholder="Yil"
          >
            <el-option v-for="y in years" :key="y" :value="y" :label="y" />
          </el-select>
        </el-col>
        <el-col :span="12">
          <label class="mfr-field__label" for="mfr-quarter">Chorak</label>
          <el-select
            id="mfr-quarter"
            v-model="quarter"
            :disabled="lockPeriod || busy"
            style="width: 100%"
            placeholder="Chorak"
          >
            <el-option v-for="q in quarters" :key="q" :value="q" :label="`${q}-chorak`" />
          </el-select>
        </el-col>
      </el-row>

      <el-upload
        ref="uploadRef"
        class="mfr-dialog__upload"
        :action="action"
        :headers="headers"
        :data="uploadData"
        name="file"
        :multiple="false"
        :limit="1"
        :auto-upload="false"
        :disabled="busy"
        :on-exceed="onExceed"
        :on-change="onFileChange"
        :on-remove="onFileRemove"
        :on-progress="onProgress"
        :on-success="onUploadSuccess"
        :on-error="onUploadError"
        :before-upload="beforeUpload"
        drag
      >
        <el-icon class="el-icon--upload"><upload-filled /></el-icon>
        <div class="el-upload__text">Faylni tashlang yoki <em>bu yerga bosing</em></div>
        <template #tip>
          <div class="el-upload__tip">Bitta fayl, maksimal hajm — 20 MB</div>
        </template>
      </el-upload>

      <div v-if="selectedFile" class="mfr-dialog__file">
        <div class="mfr-dialog__file-name" :title="selectedFile.name">{{ selectedFile.name }}</div>
        <div class="mfr-dialog__file-meta">
          {{ selectedFile.type || "noma'lum turdagi fayl" }} · {{ formatSize(selectedFile.size) }}
        </div>
      </div>

      <div v-if="step === 'uploading'" class="mfr-dialog__progress">
        <el-progress :percentage="progress" :stroke-width="8" />
        <div class="mfr-dialog__progress-row">
          <span aria-live="polite">Yuklanmoqda: {{ progress }}%</span>
          <el-button link type="danger" @click="cancelUpload">Bekor qilish</el-button>
        </div>
      </div>

      <div v-if="step === 'linking'" class="mfr-dialog__status" aria-live="polite">
        Davrga bog'lanmoqda...
      </div>

      <el-alert
        v-if="linkError"
        type="error"
        show-icon
        :closable="false"
        class="mfr-dialog__notice"
        title="Bog'lash amalga oshmadi. Qayta urinib ko'ring"
        :description="linkError"
      />
    </div>

    <template #footer>
      <el-button :disabled="busy" @click="dialogVisible = false">Bekor qilish</el-button>
      <el-button type="primary" :loading="busy" :disabled="!canSubmit" @click="submit">
        {{ uploadedFileId ? "Qayta bog'lash" : "Saqlash" }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ElMessage } from "element-plus";
import { UploadFilled } from "@element-plus/icons-vue";
import api, {
  QUARTERS,
  MAX_FILE_SIZE,
  formatFileSize,
  uploadUrl,
  uploadHeaders,
} from "./mahallaFundReportsApi";

export default {
  name: "MahallaFundReportUploadDialog",
  components: { UploadFilled },
  props: {
    modelValue: { type: Boolean, default: false },
    defaultYear: { type: Number, default: null },
    defaultQuarter: { type: Number, default: null },
    // Mavjud rowdan ochilganda yil/chorak readonly bo'ladi.
    lockPeriod: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "saved"],
  data() {
    return {
      year: this.defaultYear || new Date().getFullYear(),
      quarter: this.defaultQuarter || 1,
      selectedFile: null,
      progress: 0,
      // idle -> uploading -> linking; uploadedFileId saqlanadi, shunda PUT
      // xatosida faylni qayta yuklamasdan retry qilamiz.
      step: "idle",
      uploadedFileId: null,
      linkError: null,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    quarters() {
      return QUARTERS;
    },
    years() {
      const current = new Date().getFullYear();
      const list = [];
      for (let y = current + 1; y >= 2020; y--) list.push(y);
      return list;
    },
    action() {
      return uploadUrl();
    },
    headers() {
      return uploadHeaders();
    },
    uploadData() {
      return { file_name: `mahalla-sarfi-${this.year}-q${this.quarter}` };
    },
    busy() {
      return this.step !== "idle";
    },
    canSubmit() {
      if (this.busy) return false;
      if (!this.year || !this.quarter) return false;
      // Upload o'tib, faqat PUT xato bo'lgan holat — fayl qayta tanlanmasa ham
      // submit qilinadi.
      return !!this.selectedFile || !!this.uploadedFileId;
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.year = this.defaultYear || new Date().getFullYear();
        this.quarter = this.defaultQuarter || 1;
      }
    },
  },
  methods: {
    formatSize(bytes) {
      return formatFileSize(bytes);
    },
    reset() {
      this.selectedFile = null;
      this.progress = 0;
      this.step = "idle";
      this.uploadedFileId = null;
      this.linkError = null;
      this.$refs.uploadRef?.clearFiles();
    },
    onExceed() {
      ElMessage.warning("Faqat bitta fayl yuklash mumkin!");
    },
    onFileChange(file) {
      // el-upload xato bo'lgan faylni ham on-change orqali qaytaradi (on-error'dan
      // keyin, nextTick'da) — uni yangi tanlangan fayl deb hisoblamaymiz, aks holda
      // xatodan keyin tozalangan holat yana tiklanib qoladi.
      if (file.status === "fail") return;
      if (file.size > MAX_FILE_SIZE) {
        ElMessage.error("Fayl hajmi 20 MB limitdan oshdi");
        this.$refs.uploadRef?.clearFiles();
        this.selectedFile = null;
        return;
      }
      this.selectedFile = file.raw || file;
      // Yangi fayl tanlansa avvalgi upload natijasi kuchini yo'qotadi.
      this.uploadedFileId = null;
      this.linkError = null;
    },
    onFileRemove() {
      this.selectedFile = null;
      this.uploadedFileId = null;
    },
    beforeUpload(rawFile) {
      if (rawFile.size > MAX_FILE_SIZE) {
        ElMessage.error("Fayl hajmi 20 MB limitdan oshdi");
        this.step = "idle";
        return false;
      }
      return true;
    },
    submit() {
      if (!this.canSubmit) return;
      this.linkError = null;
      // Fayl allaqachon yuklangan — faqat PUT retry qilinadi.
      if (this.uploadedFileId) {
        this.link();
        return;
      }
      const raw = this.selectedFile;
      const upload = this.$refs.uploadRef;
      if (!raw || !upload) return;
      this.progress = 0;
      this.step = "uploading";
      // el-upload faqat `ready` statusdagi fayllarni yuboradi va xato/abort/success
      // dan keyin fayl bu statusda qolmaydi (xatoda ichki ro'yxatdan butunlay
      // o'chiriladi). Retryda ro'yxat bo'sh bo'lsa submit() jimgina hech nima
      // qilmaydi va dialog "Yuklanmoqda: 0%" da qotib qoladi — shuning uchun har
      // safar ro'yxatni tanlangan fayl bilan qaytadan tiklaymiz.
      upload.clearFiles();
      upload.handleStart(raw);
      upload.submit();
    },
    cancelUpload() {
      this.$refs.uploadRef?.abort();
      this.step = "idle";
      this.progress = 0;
    },
    onProgress(event) {
      this.progress = Math.min(99, Math.round(event.percent || 0));
    },
    onUploadSuccess(response) {
      // Keyingi request uchun faqat opaque `id` ishlatiladi (slug/URL emas).
      const fileId = response?.id;
      if (!fileId) {
        this.step = "idle";
        ElMessage.error("Upload javobida fayl identifikatori topilmadi");
        return;
      }
      this.progress = 100;
      this.uploadedFileId = fileId;
      this.link();
    },
    onUploadError(error) {
      this.step = "idle";
      this.progress = 0;
      ElMessage.error(this.uploadErrorMessage(error));
      // Fayl qayta tanlanadi — el-upload'da xato holatidagi fayl qoladi.
      this.$refs.uploadRef?.clearFiles();
      this.selectedFile = null;
    },
    uploadErrorMessage(error) {
      const status = error?.status;
      if (status === 413) return "Fayl hajmi 20 MB limitdan oshdi";
      if (status === 401) return "Sessiya tugadi. Qaytadan kiring";
      if (status === 403) return "Sizda ushbu amal uchun huquq yo'q";
      let message = null;
      try {
        message = JSON.parse(error?.message)?.message;
      } catch (e) {
        // JSON emas — quyidagi umumiy xabar ishlatiladi
      }
      if (status === 415) return message || "Fayl formati qo'llab-quvvatlanmaydi";
      if (status >= 500) return "Serverda xatolik. Keyinroq urinib ko'ring";
      return message || "Fayl yuklashda xatolik. Qayta urinib ko'ring";
    },
    link() {
      this.step = "linking";
      api
        .attach({ year: this.year, quarter: this.quarter, file_id: this.uploadedFileId })
        .then(() => {
          this.step = "idle";
          ElMessage.success("Hisobot saqlandi");
          this.$emit("saved");
          this.dialogVisible = false;
        })
        .catch((error) => {
          // PUT xato bo'lsa avvalgi active hisobot o'zgarmagan — dialog ochiq
          // qoladi va aynan shu file_id bilan retry qilinadi.
          this.step = "idle";
          this.linkError = this.linkErrorMessage(error);
        });
    },
    linkErrorMessage(error) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      if (status === 400) return message || "Yuborilgan ma'lumotlar noto'g'ri";
      if (status === 403) return "Sizda ushbu amal uchun huquq yo'q";
      if (status === 404) return "Fayl topilmadi";
      if (status >= 500) return "Serverda xatolik. Keyinroq urinib ko'ring";
      return message || "Tarmoq xatosi";
    },
  },
};
</script>

<style scoped>
.mfr-field__label {
  display: block;
  margin-bottom: 6px;
  color: #606266;
  font-size: 13px;
}
.mfr-dialog__notice {
  margin-bottom: 14px;
}
.mfr-dialog__upload {
  margin-top: 16px;
}
.mfr-dialog__file {
  margin-top: 12px;
  padding: 10px 12px;
  background: #f5f7fa;
  border-radius: 6px;
}
.mfr-dialog__file-name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mfr-dialog__file-meta {
  margin-top: 2px;
  color: #909399;
  font-size: 12px;
}
.mfr-dialog__progress {
  margin-top: 14px;
}
.mfr-dialog__progress-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
  font-size: 13px;
  color: #606266;
}
.mfr-dialog__status {
  margin-top: 14px;
  font-size: 13px;
  color: #606266;
}
</style>
